<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="hs-header">
        <span class="hs-cap">HOT SEAT</span>
        <div class="hs-timer">
          <svg viewBox="0 0 36 36" class="hs-timer-svg">
            <circle
              cx="18"
              cy="18"
              r="15"
              class="hs-timer-track"
            />
            <circle
              cx="18"
              cy="18"
              r="15"
              class="hs-timer-progress"
            />
          </svg>
          <span class="hs-timer-num">12</span>
        </div>
      </div>
      <div class="hs-question">
        <span class="hs-bar hs-bar--xl"></span>
        <span class="hs-bar hs-bar--lg"></span>
        <span class="hs-bar hs-bar--md"></span>
      </div>
      <div class="hs-player">
        <span class="hs-avatar"></span>
        <div class="hs-player-meta">
          <span class="hs-cap-sm">PLAYER UP</span>
          <span class="hs-name">Theo</span>
        </div>
      </div>
    </template>

    <template #audience>
      <div class="hs-phone-banner">
        <span class="material-symbols-rounded">whatshot</span>
        <span class="hs-phone-banner-text">YOU'RE ON!</span>
      </div>
      <span class="hs-phone-q hs-bar"></span>
      <div class="hs-phone-input">
        <span class="hs-phone-typed"><span class="hs-caret">|</span></span>
      </div>
      <button type="button" class="hs-phone-send" tabindex="-1">
        Submit
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewHotSeat',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
})
</script>

<style scoped lang="scss">
.hs-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.hs-bar--xl { width: 90%; }
.hs-bar--lg { width: 70%; }
.hs-bar--md { width: 45%; }

.hs-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
}
.hs-cap {
  font-size: 11px; font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, white);
  padding: 4px 10px;
  border-radius: 999px;
}
.hs-timer {
  position: relative;
  width: 56px; height: 56px;
}
.hs-timer-svg {
  width: 100%; height: 100%;
  transform: rotate(-90deg);
}
.hs-timer-track {
  fill: none; stroke: rgba(15, 15, 20, 0.08);
  stroke-width: 3;
}
.hs-timer-progress {
  fill: none; stroke: var(--accent);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 94.25;
  stroke-dashoffset: 0;
  animation: hs-tick 4s linear infinite;
}
.hs-timer-num {
  position: absolute;
  inset: 0;
  display: grid; place-items: center;
  font-size: 16px; font-weight: 800;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}

.hs-question {
  display: flex; flex-direction: column; gap: 5px;
  padding: 6px 0;
}
.hs-question .hs-bar { background: rgba(15, 15, 20, 0.2); }

.hs-player {
  display: flex; align-items: center; gap: 10px;
  margin-top: auto;
  padding-top: 6px;
  border-top: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}
.hs-avatar {
  width: 30px; height: 30px;
  border-radius: 999px;
  background: var(--accent);
  animation: hs-glow 4s ease-in-out infinite;
}
.hs-player-meta {
  display: flex; flex-direction: column; gap: 2px;
}
.hs-cap-sm {
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(15, 15, 20, 0.45);
}
.hs-name {
  font-size: 14px; font-weight: 800;
  color: rgba(15, 15, 20, 0.85);
}

/* ---- Audience phone ---- */
.hs-phone-banner {
  background: var(--accent);
  color: white;
  border-radius: 8px;
  padding: 8px 6px;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  animation: hs-flash 4s ease-in-out infinite;
}
.hs-phone-banner .material-symbols-rounded { font-size: 18px; }
.hs-phone-banner-text {
  font-size: 12px; font-weight: 800;
  letter-spacing: 0.06em;
}
.hs-phone-q {
  height: 8px; width: 70%;
  background: rgba(15, 15, 20, 0.18);
}
.hs-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 12px 10px;
  min-height: 40px;
  background: white;
}
.hs-phone-typed {
  font-size: 14px; font-weight: 700;
  color: rgba(15, 15, 20, 0.85);
}
.hs-caret {
  color: var(--accent);
  animation: hs-blink 1s steps(2) infinite;
}
.hs-phone-send {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

@keyframes hs-tick {
  0%   { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 94.25; }
}
@keyframes hs-glow {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  50%      { box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent) 0%, transparent); }
}
@keyframes hs-flash {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.03); }
}
@keyframes hs-blink {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .hs-timer-progress, .hs-avatar, .hs-phone-banner, .hs-caret { animation: none; }
}
</style>
